import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface RelatedApp {
  platform: string;
  url?: string;
  id?: string;
}

const isAndroid = () => /android/i.test(navigator.userAgent);
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isChromeIOS = () => /CriOS/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

export function useInstallPrompt() {
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (!isAndroid()) {
      setIsChecking(false);
      return;
    }

    const checkInstalled = async () => {
      try {
        if ('getInstalledRelatedApps' in navigator) {
          const relatedApps: RelatedApp[] = await (navigator as Navigator & {
            getInstalledRelatedApps(): Promise<RelatedApp[]>;
          }).getInstalledRelatedApps();
          setIsInstalled(relatedApps.some((app) => app.platform === 'webapp'));
        }
      } catch {
        // API unavailable or failed — assume not installed so item remains visible
      } finally {
        setIsChecking(false);
      }
    };

    void checkInstalled();

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPromptEvent(e as BeforeInstallPromptEvent);
      setIsInstalled(false);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const triggerInstall = async () => {
    if (!installPromptEvent) return;
    const eventToPrompt = installPromptEvent;
    setInstallPromptEvent(null);
    await eventToPrompt.prompt();
    await eventToPrompt.userChoice;
  };

  return {
    showInstallOption: isAndroid() && !isChecking && !isInstalled,
    hasNativePrompt: installPromptEvent !== null,
    triggerInstall,
    isIOS: isIOS(),
    isIOSSafari: isIOS() && !isChromeIOS() && !isStandalone(),
    isChromeIOS: isChromeIOS() && !isStandalone(),
  };
}
